<template>
  <v-sheet
    outlined
    rounded
    class="live-queue-strip"
    :class="{ 'live-queue-strip--collapsed': collapsed, 'live-queue-strip--paused': paused }"
    data-testid="live-queue-strip"
  >
    <div class="live-queue-strip__bar" @click="toggleCollapsed">
      <v-icon small color="primary" class="mr-1">mdi-tray-full</v-icon>
      <span class="subtitle-2 live-queue-strip__title">Live queue</span>
      <v-chip
        x-small
        class="ml-2 live-queue-strip__running-count"
        :color="running.length ? 'primary' : 'grey lighten-2'"
        :dark="running.length > 0"
      >
        {{ running.length }} running
      </v-chip>
      <v-chip x-small outlined class="ml-1 live-queue-strip__waiting-count">
        {{ waiting.length }} waiting
      </v-chip>
      <span
        v-if="nextWaiting"
        class="caption grey--text text--darken-1 ml-2 live-queue-strip__next"
      >
        next: {{ nextLabel }}
      </span>
      <v-spacer />
      <span
        class="caption live-queue-strip__slots"
        :title="`Capacity source: ${slotsSourceLabel}`"
        data-testid="live-queue-slots"
      >
        <v-icon x-small class="mr-1">mdi-server</v-icon>
        slots {{ slotsText }}
        <span class="grey--text">({{ slotsSourceLabel }})</span>
      </span>
      <v-chip
        x-small
        outlined
        class="ml-2 live-queue-strip__worker"
        :color="workerVersion.degraded ? 'warning' : undefined"
        :title="workerVersion.title"
      >
        worker {{ workerVersion.text }}
      </v-chip>
      <v-chip v-if="paused" x-small color="warning" dark class="ml-2 live-queue-strip__paused">
        <v-icon x-small left>mdi-pause-circle</v-icon>
        Paused
      </v-chip>
      <v-btn
        x-small
        outlined
        :color="paused ? 'success' : 'warning'"
        class="ml-2 live-queue-strip__pause-toggle"
        :loading="pauseBusy"
        :disabled="pauseBusy"
        @click.stop="togglePause"
      >
        <v-icon x-small left>{{ paused ? 'mdi-play' : 'mdi-pause' }}</v-icon>
        {{ paused ? 'Resume' : 'Pause' }}
      </v-btn>
      <v-btn
        icon
        x-small
        class="ml-1 live-queue-strip__open"
        title="Open the analysis queue"
        aria-label="Open the analysis queue"
        @click.stop="openQueuePage"
      >
        <v-icon small>mdi-open-in-new</v-icon>
      </v-btn>
      <v-btn
        icon
        x-small
        class="ml-1 live-queue-strip__toggle"
        :title="collapsed ? 'Expand' : 'Collapse'"
        :aria-label="collapsed ? 'Expand live queue' : 'Collapse live queue'"
        :aria-expanded="String(!collapsed)"
        @click.stop="toggleCollapsed"
      >
        <v-icon small>{{ collapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
      </v-btn>
    </div>

    <v-expand-transition>
      <div v-show="!collapsed" class="live-queue-strip__body" data-testid="live-queue-body">
        <div v-if="error && !hasRuns" class="caption error--text live-queue-strip__error">
          {{ error }}
        </div>
        <div v-else-if="!hasRuns" class="caption grey--text text--darken-1 live-queue-strip__empty">
          Nothing running or waiting
        </div>
        <template v-else>
          <div v-if="running.length" class="live-queue-strip__running">
            <QueueRunCard
              v-for="run in running"
              :key="runKey(run)"
              :run="run"
              :now="now"
              :pending="isPending(runKey(run))"
              :force-enabled="false"
              class="live-queue-strip__card"
              @cancel="askCancel"
              @mark-failed="openQueuePage"
              @open="openRun"
            />
          </div>
          <div
            v-if="waiting.length"
            class="caption grey--text text--darken-1 live-queue-strip__waiting"
            data-testid="live-queue-waiting"
          >
            <v-icon x-small class="mr-1">mdi-timer-sand</v-icon>
            {{ waiting.length }} waiting · next: {{ nextLabel }}
            <template v-if="thenLabels">· then {{ thenLabels }}</template>
          </div>
        </template>
        <div class="caption grey--text live-queue-strip__footer">
          <span v-if="lastUpdatedAt">updated {{ formatAge(lastUpdatedAt, now) }}</span>
          <span v-if="!live" class="ml-2 warning--text">live updates paused</span>
        </div>
      </div>
    </v-expand-transition>

    <v-dialog v-model="cancelDialog.open" max-width="440">
      <v-card class="live-queue-strip__cancel-dialog">
        <v-card-title class="py-3">Cancel {{ runLabel(cancelDialog.run) }}?</v-card-title>
        <v-card-text>
          The worker stops at its next checkpoint and partial results are discarded. The video
          returns to its previous status.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelDialog.open = false">Keep running</v-btn>
          <v-btn
            color="warning"
            depressed
            class="live-queue-strip__cancel-confirm"
            @click="confirmCancel"
          >
            Cancel run
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.open" :timeout="5000" :color="snackbar.color" bottom>
      {{ snackbar.text }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.open = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-sheet>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { describeActionError, formatAge, sourceLabel, sourceRoute } from './queueFormat';
import QueueRunCard from './QueueRunCard.vue';
import { runIdOf } from '@/store/modules/analysisQueue';

export const COLLAPSED_STORAGE_KEY = 'vp.liveQueueStrip.collapsed';
const AGE_TICK_MS = 15000;

const SLOT_SOURCE_LABELS = Object.freeze({
  workers: 'live workers',
  cap: 'configured cap',
  unknown: 'unknown',
});

function readCollapsed(storageKey) {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(storageKey) === '1';
  } catch (_error) {
    return false;
  }
}

function writeCollapsed(storageKey, collapsed) {
  try {
    if (typeof localStorage !== 'undefined')
      localStorage.setItem(storageKey, collapsed ? '1' : '0');
  } catch (_error) {
    // Private mode / quota: the preference simply does not persist.
  }
}

/**
 * Compact live view of the analysis queue for list pages (M3 task F11): running cards with
 * stage / % / Cancel, "N waiting · next: …", slots (capacity, in use, source), worker version and
 * Pause / Resume. Fed by the `analysisQueue` store (socket changes + polling while visible);
 * collapsed state persists in localStorage.
 */
export default {
  name: 'LiveQueueStrip',

  components: { QueueRunCard },

  props: {
    /** localStorage key of the collapsed preference (one per page if needed). */
    storageKey: {
      type: String,
      default: COLLAPSED_STORAGE_KEY,
    },
    /** Route of the full queue page. */
    queueRoute: {
      type: String,
      default: '/analysis-queue',
    },
  },

  data() {
    return {
      collapsed: readCollapsed(this.storageKey),
      now: Date.now(),
      ageTimer: null,
      pauseBusy: false,
      cancelDialog: { open: false, run: null },
      snackbar: { open: false, text: '', color: 'error' },
    };
  },

  computed: {
    ...mapState('analysisQueue', [
      'waiting',
      'running',
      'slots',
      'versions',
      'paused',
      'live',
      'lastUpdatedAt',
      'error',
    ]),
    ...mapGetters('analysisQueue', ['isPending', 'nextWaiting']),
    hasRuns() {
      return this.running.length + this.waiting.length > 0;
    },
    nextLabel() {
      return this.nextWaiting ? sourceLabel(this.nextWaiting) : '';
    },
    thenLabels() {
      const rest = this.waiting.slice(1, 3).map((run) => sourceLabel(run));
      if (!rest.length) return '';
      const more = this.waiting.length - 3;
      return more > 0 ? `${rest.join(', ')} and ${more} more` : rest.join(', ');
    },
    slotsText() {
      const { capacity, in_use: inUse } = this.slots || {};
      if (capacity == null) return inUse != null ? `${inUse} in use` : 'unknown';
      return `${inUse || 0} / ${capacity}`;
    },
    slotsSourceLabel() {
      return SLOT_SOURCE_LABELS[this.slots?.source] || SLOT_SOURCE_LABELS.unknown;
    },
    workerVersion() {
      const worker = this.versions?.worker;
      if (!worker)
        return { text: 'no run yet', title: 'No worker has reported yet', degraded: false };
      const sha = worker.build_sha ? String(worker.build_sha).slice(0, 7) : '';
      return {
        text: `${worker.tool_version || '?'}${sha ? ` · ${sha}` : ''}`,
        title: `Worker ${worker.tool_version || ''} build ${worker.build_sha || ''}`,
        degraded: Boolean(worker.degraded),
      };
    },
  },

  mounted() {
    this.startLive();
    this.ageTimer = setInterval(() => {
      this.now = Date.now();
    }, AGE_TICK_MS);
  },

  beforeDestroy() {
    this.stopLive();
    if (this.ageTimer) clearInterval(this.ageTimer);
  },

  methods: {
    ...mapActions('analysisQueue', ['startLive', 'stopLive', 'cancel', 'pause', 'resume']),
    formatAge,
    runKey(run) {
      return runIdOf(run);
    },
    runLabel(run) {
      return run ? sourceLabel(run) : 'run';
    },
    toggleCollapsed() {
      this.collapsed = !this.collapsed;
      writeCollapsed(this.storageKey, this.collapsed);
    },
    openQueuePage() {
      if (this.$router) this.$router.push(this.queueRoute).catch(() => {});
    },
    openRun(run) {
      const route = sourceRoute(run);
      if (route && this.$router) this.$router.push(route).catch(() => {});
    },
    notify(text, color = 'error') {
      this.snackbar = { open: true, text, color };
    },
    askCancel(run) {
      this.cancelDialog = { open: true, run };
    },
    async confirmCancel() {
      const run = this.cancelDialog.run;
      this.cancelDialog = { open: false, run: null };
      if (!run) return;
      try {
        await this.cancel(runIdOf(run));
        this.notify(`Cancel requested for ${this.runLabel(run)}`, 'success');
      } catch (error) {
        this.notify(describeActionError(error, 'cancel'));
      }
    },
    async togglePause() {
      if (this.pauseBusy) return;
      this.pauseBusy = true;
      const pausing = !this.paused;
      try {
        await (pausing ? this.pause() : this.resume());
        this.notify(pausing ? 'Queue paused' : 'Queue resumed', 'success');
      } catch (error) {
        this.notify(describeActionError(error, pausing ? 'pause' : 'resume'));
      } finally {
        this.pauseBusy = false;
      }
    },
  },
};
</script>

<style scoped>
.live-queue-strip {
  padding: 0;
}
.live-queue-strip--paused {
  border-color: var(--v-warning-base, #fb8c00) !important;
}
.live-queue-strip__bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 0;
  padding: 6px 10px;
  cursor: pointer;
  user-select: none;
}
.live-queue-strip__title {
  font-weight: 500;
}
.live-queue-strip__slots {
  font-variant-numeric: tabular-nums;
  display: inline-flex;
  align-items: center;
}
.live-queue-strip__body {
  padding: 4px 10px 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.live-queue-strip__running {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 0 8px;
}
.live-queue-strip__card {
  margin-bottom: 6px;
}
.live-queue-strip__waiting {
  display: flex;
  align-items: center;
  margin-top: 2px;
}
.live-queue-strip__footer {
  margin-top: 2px;
}
</style>
